import React, { useState, useMemo } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface PDFThumbnailProps {
  file: string; // base64 data URL
  onClick?: () => void;
  maxHeight?: number;
}

const PDFThumbnail: React.FC<PDFThumbnailProps> = ({ 
  file, 
  onClick,
  maxHeight = 200 
}) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [error, setError] = useState<boolean>(false);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setError(false);
  };

  const onDocumentLoadError = (error: Error) => {
    console.error('PDF load error:', error);
    setError(true);
  };

  // If the "base64" portion is actually a GCS or other URL, extract and convert to HTTPS
  const gcsUrl = useMemo(() => {
    if (file.startsWith('data:')) {
      const base64Data = file.split(',')[1];
      if (base64Data && base64Data.startsWith('gs://')) {
        // Convert gs://bucket/path -> https://storage.googleapis.com/bucket/path
        return base64Data.replace(/^gs:\/\//, 'https://storage.googleapis.com/');
      }
    }
    return null;
  }, [file]);

  // Convert base64 data URL to binary data for react-pdf (memoized to prevent re-renders)
  const fileData = useMemo(() => {
    if (gcsUrl) {
      return { url: gcsUrl };
    }
    if (file.startsWith('data:')) {
      // Extract base64 data from data URL
      const base64Data = file.split(',')[1];
      if (base64Data) {
        try {
          // Remove any whitespace or invalid characters
          const cleanBase64 = base64Data.replace(/\s/g, '');
          // Convert base64 to binary string
          const binaryString = atob(cleanBase64);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          return { data: bytes };
        } catch (error) {
          console.error('Failed to decode base64 PDF:', error);
          // Fall back to using the data URL directly
          return { url: file };
        }
      }
    }
    return { url: file };
  }, [file, gcsUrl]);

  if (error) {
    // Fallback to link if PDF fails to load or if file is a GCS/remote URL
    const href = gcsUrl ?? file;
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center px-3 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white transition-colors"
        download="uploaded.pdf"
      >
        📄 View PDF
      </a>
    );
  }

  return (
    <div 
      className="relative rounded-lg overflow-hidden shadow-sm cursor-pointer hover:opacity-90 transition-opacity bg-white"
      onClick={onClick}
      style={{ maxHeight: `${maxHeight}px` }}
    >
      <Document
        file={fileData}
        onLoadSuccess={onDocumentLoadSuccess}
        onLoadError={onDocumentLoadError}
        loading={
          <div className="flex items-center justify-center p-8 bg-gray-100">
            <div className="text-gray-500 text-sm">Loading PDF...</div>
          </div>
        }
      >
        <Page
          pageNumber={1}
          height={maxHeight}
          renderTextLayer={false}
          renderAnnotationLayer={false}
        />
      </Document>
      {numPages && numPages > 1 && (
        <div className="absolute bottom-2 right-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded">
          {numPages} pages
        </div>
      )}
    </div>
  );
};

export default PDFThumbnail;
